/* ═══════════════════════════════════════════════════════════════
   santé shared interactions
   ───────────────────────────────────────────────────────────────
   Single source of truth for reveal observers, scroll behaviours,
   and any shared client-side logic the custom sections rely on.

   Loaded once from layout/theme.liquid (deferred). Sections add
   data-attributes to mark elements for observation rather than
   wiring their own observers each time.

   Conventions:
     data-reveal                — element gets .vis on intersection
     data-reveal-children       — children with .sante-reveal-word get
                                  .vis in sequence (--stagger-word delay)
     data-reveal-once="true"    — default; observer disconnects after firing
     data-reveal-threshold="N"  — intersection threshold 0–1 (default 0.1)
     data-reveal-stagger="120"  — override stagger ms for staggered children

   Manual API (for sections that need imperative control):
     window.SanteInteractions.reveal(element)
     window.SanteInteractions.stagger(elements, delayMs)
═══════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

  /* ──────────────────────────────────────────────
     Simple reveal — adds .vis on first intersection
     ────────────────────────────────────────────── */
  function setupReveal() {
    var elements = document.querySelectorAll('[data-reveal]');
    if (!elements.length) return;

    // Group elements by threshold so each unique threshold gets its own observer
    var byThreshold = {};
    elements.forEach(function (el) {
      var t = parseFloat(el.dataset.revealThreshold) || 0.1;
      var key = String(t);
      byThreshold[key] = byThreshold[key] || { threshold: t, els: [] };
      byThreshold[key].els.push(el);
    });

    Object.keys(byThreshold).forEach(function (k) {
      var group = byThreshold[k];
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('vis');
            if (entry.target.dataset.revealOnce !== 'false') {
              observer.unobserve(entry.target);
            }
          }
        });
      }, { threshold: group.threshold });
      group.els.forEach(function (el) { observer.observe(el); });
    });
  }

  /* ──────────────────────────────────────────────
     Staggered children — type-that-arrives word reveals
     Container has data-reveal-children; children with
     .sante-reveal-word receive .vis in sequence.
     ────────────────────────────────────────────── */
  function setupStaggered() {
    var containers = document.querySelectorAll('[data-reveal-children]');
    if (!containers.length) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var stagger = parseInt(entry.target.dataset.revealStagger, 10) || 120;
        var children = entry.target.querySelectorAll('.sante-reveal-word');
        children.forEach(function (child, i) {
          setTimeout(function () { child.classList.add('vis'); }, i * stagger);
        });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1 });

    containers.forEach(function (el) { observer.observe(el); });
  }

  /* ──────────────────────────────────────────────
     Word-splitting helper — for sections that want to split a string
     into per-word spans at runtime. Adds .sante-reveal-word to each.
     ────────────────────────────────────────────── */
  function splitToWords(element, options) {
    if (!element) return;
    options = options || {};
    var text = element.textContent;
    var words = text.split(/\s+/).filter(Boolean);
    element.textContent = '';
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'sante-reveal-word';
      if (options.italicWord && options.italicWord(word, i)) {
        span.className += ' em';
      }
      span.textContent = word;
      element.appendChild(span);
      if (i < words.length - 1) element.appendChild(document.createTextNode(' '));
    });
  }

  /* ──────────────────────────────────────────────
     Public API
     ────────────────────────────────────────────── */
  window.SanteInteractions = {
    reveal: function (el) {
      if (el && el.classList) el.classList.add('vis');
    },
    stagger: function (children, delayMs) {
      var d = delayMs || 120;
      Array.prototype.forEach.call(children, function (c, i) {
        setTimeout(function () { c.classList.add('vis'); }, i * d);
      });
    },
    splitToWords: splitToWords
  };

  /* ──────────────────────────────────────────────
     Init
     ────────────────────────────────────────────── */
  function init() {
    setupReveal();
    setupStaggered();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
